import { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { RootState } from '../../../lib/store';

import iconDownBlue from '../../../assets/img/icon-sort-down_blue_24px.svg';
import iconDownGray from '../../../assets/img/icon-sort-down_gray_24px.svg';
import iconUpBlue from '../../../assets/img/icon-sort-up_blue_24px.svg';
import iconUpGray from '../../../assets/img/icon-sort-up_gray_24px.svg';
import { useIsMobile } from '../../../hooks';
import HeaderSortIcons from '../../HeaderSortIcons/HeaderSortIcons';
import SymbolTable from '../../SymbolTable/SymbolTable';
import { SymbolTableGrid } from '../../SymbolTable/SymbolTableGrid.enum';
import { WATCH_LIST_TABLE_HEADERS } from '../WatchList.constans';
import { WatchListHeaderEnum, WatchListSortedStocks, WatchListSortingType, WatchListStocks } from '../WatchList.types';

import WatchListRow from './WatchlListRow';

import styles from './WatchListTable.module.scss';

type Props = {
  sortingType?: null | WatchListSortingType;
  isLoading?: boolean;
  watchListStocks: WatchListStocks[];
  hasSwipeableRows?: boolean;
  title?: string;
  onSymbolRemoved?: (symbol: string) => void;
} & ReduxProps;

const WatchListTable = ({
  isLoading,
  sortingType,
  watchListStocks,
  hasSwipeableRows,
  title,
  onSymbolRemoved,
}: Props) => {
  const { isMobile } = useIsMobile();
  const [isAscendingOrderLastPrice, setIsAscendingOrderLastPrice] = useState<null | boolean>(null);
  const [isAscendingOrderChange, setIsAscendingOrderChange] = useState<null | boolean>(null);
  const [isAscendingOrderPercentChange, setIsAscendingOrderPercentChange] = useState<null | boolean>(null);
  const [isSortedStocks, setIsSortedStocks] = useState<boolean>(false);
  const [sortedStocks, setSortedStocks] = useState<WatchListSortedStocks[]>([]);
  const [swipedRow, setSwipedRow] = useState<string>('');
  const [tempDataStocks, setTempDataStocks] = useState<WatchListSortedStocks[]>([]);
  const [removedFavouriteSymbol, setRemovedFavouriteSymbol] = useState<string>('');

  useEffect(() => {
    if (!isMobile || !sortingType) {
      return;
    }

    switch (sortingType) {
      case WatchListSortingType.HighestPrice:
        handleChangeDownStates(WatchListHeaderEnum.COLUMN_LAST_PRICE);
        break;
      case WatchListSortingType.HighestChange:
        handleChangeDownStates(WatchListHeaderEnum.COLUMN_PERCENT_CHANGE);
        break;
      case WatchListSortingType.LowestPrice:
        handleChangeUpStates(WatchListHeaderEnum.COLUMN_LAST_PRICE);
        break;
      case WatchListSortingType.LowestChange:
        handleChangeUpStates(WatchListHeaderEnum.COLUMN_PERCENT_CHANGE);
        break;
    }
  }, [isMobile, sortingType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (removedFavouriteSymbol && onSymbolRemoved) {
      onSymbolRemoved(removedFavouriteSymbol);
      setRemovedFavouriteSymbol('');
    }
  }, [removedFavouriteSymbol, onSymbolRemoved]);

  // Function to update a specific stock in tempDataStocks
  const updateTempDataStocks = (updatedStock: WatchListSortedStocks) => {
    setTempDataStocks(prevData => {
      // Filter out stocks not in watchListStocks
      const filteredData = prevData.filter(stock =>
        watchListStocks.some(watchListStock => watchListStock.symbol === stock.symbol));

      // Add or update the stock
      const stockIndex = filteredData.findIndex(stock => stock.symbol === updatedStock.symbol);
      if (stockIndex !== -1) {
        const newData = [...filteredData];
        newData[stockIndex] = updatedStock;
        return newData;
      }

      const newData = [...filteredData, updatedStock];

      // Ensure length does not exceed watchListStocks.length
      if (newData.length > watchListStocks.length) {
        return newData.slice(-watchListStocks.length);
      }

      return newData;
    });
  };

  const handleIconUp = (item: number) => {
    switch (item) {
      case WatchListHeaderEnum.COLUMN_LAST_PRICE:
        if (isAscendingOrderLastPrice === null) {
          return iconUpGray;
        }
        return isAscendingOrderLastPrice ? iconUpBlue : iconUpGray;
      case WatchListHeaderEnum.COLUMN_CHANGE:
        if (isAscendingOrderChange === null) {
          return iconUpGray;
        }
        return isAscendingOrderChange ? iconUpBlue : iconUpGray;
      default:
        if (isAscendingOrderPercentChange === null) {
          return iconUpGray;
        }
        return isAscendingOrderPercentChange ? iconUpBlue : iconUpGray;
    }
  };

  const handleIconDown = (item: number) => {
    switch (item) {
      case WatchListHeaderEnum.COLUMN_LAST_PRICE:
        if (isAscendingOrderLastPrice === null) {
          return iconDownGray;
        }
        return !isAscendingOrderLastPrice ? iconDownBlue : iconDownGray;
      case WatchListHeaderEnum.COLUMN_CHANGE:
        if (isAscendingOrderChange === null) {
          return iconDownGray;
        }
        return !isAscendingOrderChange ? iconDownBlue : iconDownGray;
      default:
        if (isAscendingOrderPercentChange === null) {
          return iconDownGray;
        }
        return !isAscendingOrderPercentChange ? iconDownBlue : iconDownGray;
    }
  };

  const handleChangeUpStates = (currentItem: number) => {
    setIsSortedStocks(true);
    let sortedData: WatchListSortedStocks[] = [];

    // Sort current data in ascending order.
    switch (currentItem) {
      case WatchListHeaderEnum.COLUMN_LAST_PRICE:
        sortedData = tempDataStocks.slice(-watchListStocks.length).sort((a, b) => a.currentPrice - b.currentPrice);
        setSortedStocks(sortedData);
        setIsAscendingOrderLastPrice(true);
        setIsAscendingOrderChange(null);
        setIsAscendingOrderPercentChange(null);
        break;
      case WatchListHeaderEnum.COLUMN_CHANGE:
        sortedData = tempDataStocks.slice(-watchListStocks.length).sort((a, b) => a.change - b.change);
        setSortedStocks(sortedData);
        setIsAscendingOrderChange(true);
        setIsAscendingOrderLastPrice(null);
        setIsAscendingOrderPercentChange(null);
        break;
      default:
        sortedData = tempDataStocks.slice(-watchListStocks.length).sort((a, b) => a.changePercent - b.changePercent);
        setSortedStocks(sortedData);
        setIsAscendingOrderPercentChange(true);
        setIsAscendingOrderChange(null);
        setIsAscendingOrderLastPrice(null);
    }
  };

  // TODO: Consider separating interaction - sorting and tab changing - from price updates
  //       See StockList.tsx in Mobile and future updates in usage of TradePriceCache and NBBOPriceCache
  const handleChangeDownStates = (currentItem: number) => {
    setIsSortedStocks(true);
    let sortedData: WatchListSortedStocks[] = [];

    // Sort current data in descending order.
    switch (currentItem) {
      case WatchListHeaderEnum.COLUMN_LAST_PRICE:
        sortedData = tempDataStocks.slice(-watchListStocks.length).sort((a, b) => b.currentPrice - a.currentPrice);
        setSortedStocks(sortedData);
        setIsAscendingOrderLastPrice(false);
        setIsAscendingOrderChange(null);
        setIsAscendingOrderPercentChange(null);
        break;
      case WatchListHeaderEnum.COLUMN_CHANGE:
        sortedData = tempDataStocks.slice(-watchListStocks.length).sort((a, b) => b.change - a.change);
        setSortedStocks(sortedData);
        setIsAscendingOrderChange(false);
        setIsAscendingOrderLastPrice(null);
        setIsAscendingOrderPercentChange(null);
        break;
      default:
        sortedData = tempDataStocks.slice(-watchListStocks.length).sort((a, b) => b.changePercent - a.changePercent);
        setSortedStocks(sortedData);
        setIsAscendingOrderPercentChange(false);
        setIsAscendingOrderChange(null);
        setIsAscendingOrderLastPrice(null);
    }
  };

  const SORTABLE_COLUMNS: Record<number, { isAscending: boolean | null }> = {
    [WatchListHeaderEnum.COLUMN_LAST_PRICE]: {
      isAscending: isAscendingOrderLastPrice,
    },
    [WatchListHeaderEnum.COLUMN_CHANGE]: {
      isAscending: isAscendingOrderChange,
    },
    [WatchListHeaderEnum.COLUMN_PERCENT_CHANGE]: {
      isAscending: isAscendingOrderPercentChange,
    },
  };

  const tableHeaders = () => {
    const headerRowClass = hasSwipeableRows
      ? `${styles.tableHeaderRow} ${styles['tableHeaderRow--favourites']}`
      : styles.tableHeaderRow;

    return (
      <div className={headerRowClass}>
        {WATCH_LIST_TABLE_HEADERS.map((item: string, index: number) => {
          const sortableConfig = SORTABLE_COLUMNS[index];

          if (!sortableConfig) {
            return (
              <div
                key={item}
                className={styles.tableHeaderCell}
              >
                {item}
              </div>
            );
          }

          const { isAscending } = sortableConfig;
          const isActive = isAscending !== null;

          return (
            <div
              key={item}
              className={`${styles.tableHeaderCell} ${styles.sortTableHead}`}
            >
              <div className={styles.headerSortWrapper}>
                <span className={isActive ? styles.activeHeaderText : ''}>
                  {item}
                </span>

                <HeaderSortIcons
                  isAscending={isAscending}
                  iconUpSrc={handleIconUp(index)}
                  iconDownSrc={handleIconDown(index)}
                  onClickUp={() => handleChangeUpStates(index)}
                  onClickDown={() => handleChangeDownStates(index)}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const tableItems: WatchListStocks[] = isSortedStocks ? sortedStocks : watchListStocks;

  return (
    <div
      className={isMobile ? styles.wrapperMobile : styles.wrapper}
      data-title={title}
    // style={isMobile ? { maxHeight: window.innerHeight - getHeight() } : {}} TODO: Re-enable and fix in HW-886
    >
      <SymbolTable
        items={tableItems}
        renderHeaders={tableHeaders}
        isLoading={isLoading}
        skeletonGrid={SymbolTableGrid.FIVE_ROW_ITEMS_GRID}
        renderRow={(stock) => (
          <WatchListRow
            key={stock.symbol}
            stock={stock}
            hasSwipeableRows={hasSwipeableRows}
            swipedRow={swipedRow}
            setSwipedRow={setSwipedRow}
            tempDataStocks={tempDataStocks}
            updateTempDataStocks={updateTempDataStocks}
            setRemovedFavouriteSymbol={setRemovedFavouriteSymbol}
          />
        )}
      />
    </div>
  );
};

const mapStateToProps = (_state: RootState) => ({});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(WatchListTable);
